import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Car, Footprints, ArrowRight, Navigation } from "lucide-react";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";

function formatHourLine(h) {
  if (h.closed) return "Closed";
  if (!h.open || !h.close) return "By appointment";
  return `${h.open} – ${h.close}`;
}

export default function Visit() {
  const { info } = useBusinessInfo();
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">§ Visit the shop</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95]">
              Pick up your<br />prints in person.
            </h1>
            <p className="text-[#525252] mt-4 text-lg leading-relaxed">
              We&apos;re in the heart of {info.city}. Walk in any time during opening hours — your tracking number is all you need.
            </p>
          </div>

          <div className="space-y-5">
            <Row icon={MapPin} label="Address">
              <div className="font-bold" data-testid="visit-address">{info.name} · Print, Bind, Laminate</div>
              {info.address_line1 && <div className="text-[#525252]">{info.address_line1}</div>}
              {info.address_line2 && <div className="text-[#525252]">{info.address_line2}</div>}
              <div className="text-[#525252]">{[info.city, info.country].filter(Boolean).join(", ")}</div>
            </Row>
            <Row icon={Clock} label="Opening hours">
              <ul className="text-sm space-y-1" data-testid="visit-hours">
                {(info.hours || []).map((h) => (
                  <li key={h.day} className="flex items-center justify-between gap-4">
                    <span className="text-[#525252] uppercase tracking-[0.15em] text-[10px] font-bold">{h.day}</span>
                    <span className={`mono font-bold ${h.closed ? "text-[#525252]" : ""}`}>{formatHourLine(h)}</span>
                  </li>
                ))}
              </ul>
            </Row>
            <Row icon={Phone} label="Call us">
              <a href={`tel:${(info.phone || "").replace(/\s/g, "")}`} className="mono font-bold text-lg hover:text-[#059669]" data-testid="visit-phone">
                {info.phone}
              </a>
            </Row>
            <Row icon={Mail} label="Email">
              <a href={`mailto:${info.email}`} className="mono font-bold hover:text-[#059669]" data-testid="visit-email">
                {info.email}
              </a>
            </Row>
          </div>

          <div className="flex flex-wrap gap-3">
            {info.maps_directions_url && (
              <a
                href={info.maps_directions_url}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-directions"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#059669] text-white text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857]"
              >
                <Navigation className="w-4 h-4" /> Get directions
              </a>
            )}
            <Link
              to="/order"
              className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0A0A0A] text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white"
            >
              Place an order <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="aspect-[4/3] border-2 border-[#0A0A0A] overflow-hidden bg-[#F5F5F5]" data-testid="visit-map">
            {info.maps_embed_resolved ? (
              <iframe
                title={`${info.name} — shop location`}
                src={info.maps_embed_resolved}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#525252] text-sm">Map not configured</div>
            )}
          </div>

          {(info.parking_info || info.matatu_info) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {info.parking_info && (
                <InfoTile icon={Car} title="Parking" body={info.parking_info} />
              )}
              {info.matatu_info && (
                <InfoTile icon={Footprints} title="Matatu / on-foot" body={info.matatu_info} />
              )}
            </div>
          )}

          <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70 mb-2">§ Coming to collect a print?</div>
            <div className="font-display font-black text-xl mb-2">Just bring your tracking number.</div>
            <div className="text-sm opacity-80">
              Show the cashier the order code that starts with <span className="mono font-bold text-[#059669]">CTP-</span> — we&apos;ll have your job ready at the counter. No queue, no fuss.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4 border-l-2 border-[#0A0A0A] pl-4">
      <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">{label}</div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function InfoTile({ icon: Icon, title, body }) {
  return (
    <div className="border border-[#E5E5E5] bg-white p-5">
      <Icon className="w-5 h-5 text-[#059669] mb-3" strokeWidth={2.5} />
      <div className="font-display font-black text-lg mb-1">{title}</div>
      <div className="text-sm text-[#525252]">{body}</div>
    </div>
  );
}
